// client/src/components/unidades/sections/UnidadeProfissionaisSection.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useUserUnidadesByUnidade } from '../../../hooks/useRelationships';
import { useProfissionaisQuery } from '../../../hooks/useProfissionais'; // ✅ OTIMIZAÇÃO: Busca direta!
import { useRelationshipsMutations } from '../../../hooks/useRelationships';
import { useRoles } from '../../../hooks/useRoles';
import { 
    Save, Loader2, Users, XCircle, CheckCircle, Search,
    ChevronDown, AlertCircle, Settings, RefreshCw
} from 'lucide-react';
import useAlert from '../../../hooks/useAlert';
import { useTenantI18n } from '../../../hooks/useAccounts';
import { getCurrencySymbol } from '../../../utils/formatters';
import { useQueryClient } from '@tanstack/react-query';
import DesvincularUnidadeModal from '../tabs/DesvincularUnidadeModal';

// ✅ FUNÇÃO BLINDADA PARA EXTRAIR ID
const getSafeId = (item) => {
    if (!item) return null;
    if (typeof item === 'string') return item;
    if (typeof item === 'object') {
        if (item._id) return String(item._id);
        if (item.id) return String(item.id);
    }
    return String(item);
};

const UnidadeProfissionaisSection = ({ unidade, contextData }) => {
    const { setAlert } = useAlert();
    // ✅ ACHADO: rótulos abaixo eram fixos em "(R$)", sem ler
    // configuracoesGerais.i18n da conta.
    const { currency, locale } = useTenantI18n();
    const currencySymbol = getCurrencySymbol(currency, locale);
    const queryClient = useQueryClient();
    
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProfissionais, setSelectedProfissionais] = useState([]);
    const [comissoesConfig, setComissoesConfig] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [expandedProfissional, setExpandedProfissional] = useState(null);
    const [roleProfissionalId, setRoleProfissionalId] = useState(null);
    const [dadosCarregados, setDadosCarregados] = useState(false);
    const [imgErrors, setImgErrors] = useState({}); 

    const initialSyncDone = useRef(false);

    // Modal de desvinculação
    const [vinculoParaDesvincular, setVinculoParaDesvincular] = useState(null);
    const [showDesvincularModal, setShowDesvincularModal] = useState(false);

    const targetUnidadeId = getSafeId(unidade);
    const targetAccountId = getSafeId(contextData?.contaId);

    const { data: rolesData, isLoading: loadingRoles } = useRoles({
        account: targetAccountId,
        enabled: !!targetAccountId
    });

    const { 
        data: vinculosData, 
        isLoading: isLoadingVinculos,
        refetch: refetchVinculos
    } = useUserUnidadesByUnidade(targetUnidadeId, targetAccountId, {
        enabled: !!targetUnidadeId && !!targetAccountId
    });

    // ✅ OTIMIZAÇÃO APLICADA: Busca apenas profissionais ativos da clínica diretamente no backend
    const { 
        data: profData, 
        isLoading: isLoadingUsers,
        refetch: refetchProfissionais 
    } = useProfissionaisQuery({
        accountId: targetAccountId,
        status: 'ativo',
        limit: 1000 // Paginação elástica para dropdown/listagens
    }, {
        enabled: !!targetAccountId
    });

    const { 
        createUserUnidade, 
        updateUserUnidade, 
        deleteUserUnidade,
        invalidateRelationships 
    } = useRelationshipsMutations();

    useEffect(() => {
        if (rolesData) {
            let roles = [];
            if (Array.isArray(rolesData)) {
                roles = rolesData;
            } else if (rolesData.data && Array.isArray(rolesData.data)) {
                roles = rolesData.data;
            }

            const roleProfissional = roles.find(r => 
                r.name?.toLowerCase() === 'profissional' || 
                r.nome?.toLowerCase() === 'profissional'
            );

            if (roleProfissional) {
                setRoleProfissionalId(getSafeId(roleProfissional));
            }
        }
    }, [rolesData]);

    const vinculosAtivosDoBanco = useMemo(() => {
        if (!vinculosData?.data) return [];
        return vinculosData.data.filter(v => v.status === 'ativo');
    }, [vinculosData]);

    // Sincronização segura
    useEffect(() => {
        if (initialSyncDone.current || isLoadingVinculos) return;
        if (!vinculosData) return;

        if (vinculosAtivosDoBanco.length > 0) {
            const ids = vinculosAtivosDoBanco.map(v => getSafeId(v.user)).filter(Boolean);
            
            const comissoes = {};
            vinculosAtivosDoBanco.forEach(v => {
                const userId = getSafeId(v.user);
                if (userId) {
                    comissoes[userId] = v.comissao || { 
                        ativo: false, 
                        faixas: [{ type: 'percentual', valor: 0, limite: { type: 'sem_limite', valor: 0 } }],
                        tipoAplicacao: 'sobre_servico',
                        considerarTaxas: false,
                        considerarDescontos: true
                    };
                }
            });
            
            setSelectedProfissionais(ids);
            setComissoesConfig(comissoes);
        } else {
            setSelectedProfissionais([]);
            setComissoesConfig({});
        }
        
        initialSyncDone.current = true;
        setDadosCarregados(true);
    }, [vinculosAtivosDoBanco, vinculosData, isLoadingVinculos]);

    const profissionaisBase = useMemo(() => {
        if (!profData) return [];
        return Array.isArray(profData) ? profData : (profData.data || []);
    }, [profData]);

    const vinculosMap = useMemo(() => {
        const map = new Map();
        vinculosAtivosDoBanco.forEach(v => {
            const userId = getSafeId(v.user);
            if (userId) map.set(userId, v);
        });
        return map;
    }, [vinculosAtivosDoBanco]);

    const profissionaisRenderizaveis = useMemo(() => {
        if (!dadosCarregados) return [];

        let arrayFinal = profissionaisBase.map(prof => {
            const userObj = prof.user || {}; // O Backend já populará a ref do usuário aqui
            const profId = getSafeId(userObj._id || userObj.id || prof.user);
            
            const isSelected = selectedProfissionais.includes(profId);
            const vinculoNoBanco = vinculosMap.get(profId);
            const hasVinculoNoBanco = !!vinculoNoBanco;
            
            let comissaoData = comissoesConfig[profId];
            if (!comissaoData && vinculoNoBanco?.comissao) {
                comissaoData = vinculoNoBanco.comissao;
            }
            if (!comissaoData) {
                comissaoData = {
                    ativo: false,
                    tipoAplicacao: 'sobre_servico',
                    faixas: [{ type: 'percentual', valor: 0, limite: { type: 'sem_limite', valor: 0 } }],
                    considerarTaxas: false,
                    considerarDescontos: true
                };
            }

            return {
                _id: profId,
                name: userObj.name || 'Sem nome',
                email: userObj.email || '',
                photo: userObj.photoUrl || userObj.photo,
                isSelected,
                hasVinculoNoBanco,
                vinculoNoBanco,
                comissao: comissaoData
            };
        });

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            arrayFinal = arrayFinal.filter(p => 
                p.name.toLowerCase().includes(term) ||
                p.email.toLowerCase().includes(term)
            );
        }
        
        return arrayFinal;
    }, [profissionaisBase, selectedProfissionais, comissoesConfig, vinculosMap, searchTerm, dadosCarregados]);

    const profissionaisVinculados = profissionaisRenderizaveis.filter(p => p.isSelected);

    const handleToggleProfissional = (userId) => {
        if (!isEditing) return;

        const idStr = getSafeId(userId);
        const isSelected = selectedProfissionais.includes(idStr);
        const vinculoNoBanco = vinculosMap.get(idStr);

        if (isSelected) {
            if (vinculoNoBanco) {
                setVinculoParaDesvincular(vinculoNoBanco);
                setShowDesvincularModal(true);
            } else {
                const novos = selectedProfissionais.filter(id => id !== idStr);
                setSelectedProfissionais(novos);
            }
        } else {
            const novos = [...selectedProfissionais, idStr];
            setSelectedProfissionais(novos);

            setComissoesConfig(prev => {
                if (!prev[idStr]) {
                    return {
                        ...prev,
                        [idStr]: {
                            ativo: false,
                            tipoAplicacao: 'sobre_servico',
                            faixas: [{ type: 'percentual', valor: 0, limite: { type: 'sem_limite', valor: 0 } }],
                            considerarTaxas: false,
                            considerarDescontos: true
                        }
                    };
                }
                return prev;
            });
            setExpandedProfissional(idStr);
        }
    };

    const handleComissaoChange = (userId, field, value) => {
        if (!isEditing) return;
        const idStr = getSafeId(userId);
        setComissoesConfig(prev => {
            const configAtual = prev[idStr] || { ativo: false, faixas: [], tipoAplicacao: 'sobre_servico' };
            return { ...prev, [idStr]: { ...configAtual, [field]: value } };
        });
    };

    const handleFaixaChange = (userId, index, field, value) => {
        if (!isEditing) return;
        const idStr = getSafeId(userId);
        setComissoesConfig(prev => {
            const configAtual = prev[idStr] || { ativo: false, faixas: [] };
            const faixas = [...(configAtual.faixas || [])];
            
            if (!faixas[index]) {
                faixas[index] = { type: 'percentual', valor: 0, limite: { type: 'sem_limite', valor: 0 } };
            }

            if (field.includes('.')) {
                const [parent, child] = field.split('.');
                const parentObj = faixas[index][parent] || {};
                faixas[index] = { ...faixas[index], [parent]: { ...parentObj, [child]: value } };
            } else {
                faixas[index] = { ...faixas[index], [field]: value };
            }

            return { ...prev, [idStr]: { ...configAtual, faixas } };
        });
    };

    const handleAddFaixa = (userId) => {
        if (!isEditing) return;
        const idStr = getSafeId(userId);
        setComissoesConfig(prev => {
            const configAtual = prev[idStr] || { ativo: false, faixas: [] };
            const faixas = [...(configAtual.faixas || []), { type: 'percentual', valor: 0, limite: { type: 'sem_limite', valor: 0 } }];
            return { ...prev, [idStr]: { ...configAtual, faixas } };
        });
    };

    const handleRemoveFaixa = (userId, index) => {
        if (!isEditing) return;
        const idStr = getSafeId(userId);
        setComissoesConfig(prev => {
            const configAtual = prev[idStr];
            if (!configAtual || !configAtual.faixas) return prev;
            const faixas = configAtual.faixas.filter((_, idx) => idx !== index);
            return { ...prev, [idStr]: { ...configAtual, faixas } };
        });
    };

    const handleDesvincularSuccess = () => {
        invalidateRelationships();
        refetchVinculos();
        queryClient.invalidateQueries({ queryKey: ['user-unidades'] }); 
        
        if (vinculoParaDesvincular) {
            const idStr = getSafeId(vinculoParaDesvincular.user);
            setSelectedProfissionais(prev => prev.filter(id => id !== idStr));
        }
        
        setShowDesvincularModal(false);
        setVinculoParaDesvincular(null);
    };

    const handleReload = () => {
        refetchVinculos();
        refetchProfissionais(); // ✅ Recarrega a query de profissionais
        initialSyncDone.current = false;
        setDadosCarregados(false);
    };

    const handleSave = async () => {
        if (!targetUnidadeId || !targetAccountId) {
            setAlert('Dados insuficientes para salvar', 'error');
            return;
        }

        if (!roleProfissionalId) {
            setAlert('Role profissional não encontrada. Verifique as configurações.', 'error');
            return;
        }

        setIsSaving(true);

        try {
            const errors = [];

            for (const userId of selectedProfissionais) {
                try {
                    const vinculoExistente = vinculosMap.get(userId);
                    const comissao = comissoesConfig[userId] || { ativo: false, faixas: [] };

                    const payload = {
                        user: userId,
                        unidade: targetUnidadeId,
                        account: targetAccountId,
                        role: roleProfissionalId,
                        status: 'ativo',
                        comissao
                    };

                    if (vinculoExistente) {
                        await updateUserUnidade.mutateAsync({
                            id: getSafeId(vinculoExistente),
                            data: { comissao, status: 'ativo', role: roleProfissionalId }
                        });
                    } else {
                        await createUserUnidade.mutateAsync(payload);
                    }
                } catch (error) {
                    errors.push({ userId, error: error.message });
                }
            }

            invalidateRelationships();
            queryClient.invalidateQueries({ queryKey: ['user-unidades'] }); 
            await refetchVinculos();
            
            if (errors.length === 0) {
                setAlert('Profissionais da unidade atualizados com sucesso!', 'success');
            } else {
                setAlert(`Atualização concluída com erros.`, 'warning');
            }
            
            setIsEditing(false);
            setExpandedProfissional(null);
            initialSyncDone.current = false;
            setDadosCarregados(false);

        } catch (error) {
            console.error('❌ Erro ao salvar profissionais:', error);
            setAlert('Erro ao salvar configurações', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoadingUsers || isLoadingVinculos || !dadosCarregados) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader2 size={32} className="animate-spin text-primary" />
            </div>
        );
    }

    const renderProfissionalRow = (prof) => {
        const isExpanded = expandedProfissional === prof._id;
        const iniciais = prof.name ? prof.name.substring(0, 2).toUpperCase() : 'PR';
        const hasPhoto = prof.photo && !imgErrors[prof._id];
        
        return (
            <div key={prof._id} className="border-b border-gray-100 last:border-b-0 bg-white">
                <div className={`flex items-center justify-between p-4 transition-colors ${!isEditing ? 'opacity-70' : 'hover:bg-gray-50'}`}>
                    <div className="flex items-center gap-4 flex-1">
                        <button 
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleToggleProfissional(prof._id); }}
                            disabled={!isEditing}
                            className={`relative inline-flex items-center h-[22px] w-10 rounded-full transition-colors focus:outline-none flex-shrink-0 ${prof.isSelected ? 'bg-amber-400' : 'bg-gray-200'}`}
                        >
                            <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform shadow-sm ${prof.isSelected ? 'translate-x-[22px]' : 'translate-x-1'}`} />
                        </button>
                        
                        <div className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center bg-blue-100 text-blue-700 font-bold text-xs overflow-hidden border border-gray-200">
                            {hasPhoto ? (
                                <img 
                                    src={prof.photo} 
                                    alt={prof.name} 
                                    className="w-full h-full object-cover"
                                    onError={() => setImgErrors(prev => ({...prev, [prof._id]: true}))}
                                />
                            ) : (
                                iniciais
                            )}
                        </div>

                        <div className="flex-1 min-w-0">
                            <span className="text-[15px] font-medium text-gray-800 truncate block">{prof.name}</span>
                            {prof.email && <span className="text-xs text-gray-500 truncate block">{prof.email}</span>}
                        </div>
                    </div>
                    
                    <div 
                        className={`flex items-center gap-3 ${prof.isSelected && isEditing ? 'cursor-pointer group' : ''}`}
                        onClick={() => { if(isEditing && prof.isSelected) setExpandedProfissional(isExpanded ? null : prof._id) }}
                    >
                        <span className="text-xs text-gray-400 hidden sm:block">
                            {prof.comissao?.ativo ? 'Comissão definida' : 'Comissão não definida'}
                        </span>
                        {prof.isSelected && (
                            <ChevronDown size={18} className={`text-gray-400 transition-transform group-hover:text-gray-600 ${isExpanded ? 'rotate-180' : ''}`} />
                        )}
                    </div>
                </div>

                {isExpanded && prof.isSelected && (
                    <div className="p-5 bg-gray-50/50 border-t border-gray-100">
                        <div className="flex items-center gap-2 mb-4">
                            <label className="checkbox-label text-sm flex items-center cursor-pointer">
                                <div className={`relative inline-flex items-center h-[22px] w-10 rounded-full transition-colors mr-3 ${prof.comissao.ativo ? 'bg-amber-400' : 'bg-gray-300'}`}>
                                    <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform shadow-sm ${prof.comissao.ativo ? 'translate-x-[22px]' : 'translate-x-1'}`} />
                                </div>
                                <input 
                                    type="checkbox" 
                                    checked={prof.comissao.ativo || false} 
                                    onChange={(e) => handleComissaoChange(prof._id, 'ativo', e.target.checked)} 
                                    className="hidden"
                                    disabled={!isEditing}
                                />
                                <span className="font-medium text-gray-800">Ativar Comissão Personalizada nesta Unidade</span>
                            </label>
                        </div>

                        {prof.comissao.ativo && (
                            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm max-w-2xl">
                                <div className="mb-4">
                                    <label className="text-xs font-medium text-gray-600 block mb-1">Incidir Sobre</label>
                                    <select 
                                        value={prof.comissao.tipoAplicacao || 'sobre_servico'} 
                                        onChange={(e) => handleComissaoChange(prof._id, 'tipoAplicacao', e.target.value)} 
                                        disabled={!isEditing}
                                        className="form-input text-sm w-full md:w-1/2 bg-white"
                                    >
                                        <option value="sobre_servico">Valor do Serviço</option>
                                        <option value="sobre_lucro">Lucro Líquido</option>
                                        <option value="sobre_valor_total">Valor Total da Venda</option>
                                    </select>
                                </div>
                                
                                <div className="space-y-3">
                                    <label className="text-xs font-medium text-gray-600 block mb-2">Faixas de Comissão</label>
                                    {prof.comissao.faixas?.map((faixa, idx) => (
                                        <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                            <div className="grid grid-cols-12 gap-3 items-center mb-3">
                                                <div className="col-span-12 md:col-span-4">
                                                    <label className="text-[11px] text-gray-500 uppercase tracking-wider">Tipo</label>
                                                    <select
                                                        value={faixa.tipo || faixa.type || 'percentual'}
                                                        onChange={(e) => handleFaixaChange(prof._id, idx, 'tipo', e.target.value)}
                                                        disabled={!isEditing}
                                                        className="form-input text-sm w-full mt-1 bg-white"
                                                    >
                                                        <option value="percentual">Porcentagem (%)</option>
                                                        <option value="valor_fixo">Valor Fixo ({currencySymbol})</option>
                                                    </select>
                                                </div>
                                                <div className="col-span-12 md:col-span-8">
                                                    <label className="text-[11px] text-gray-500 uppercase tracking-wider">Valor</label>
                                                    <input
                                                        type="number"
                                                        value={faixa.valor || 0}
                                                        onChange={(e) => handleFaixaChange(prof._id, idx, 'valor', parseFloat(e.target.value) || 0)}
                                                        disabled={!isEditing}
                                                        className="form-input text-sm w-full mt-1"
                                                        min="0"
                                                        step={(faixa.tipo || faixa.type) === 'percentual' ? '0.1' : '0.01'}
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="grid grid-cols-12 gap-3 items-center">
                                                <div className="col-span-12 md:col-span-4">
                                                    <label className="text-[11px] text-gray-500 uppercase tracking-wider">Limite</label>
                                                    <select
                                                        value={faixa.limite?.tipo || faixa.limite?.type || 'sem_limite'}
                                                        onChange={(e) => handleFaixaChange(prof._id, idx, 'limite.tipo', e.target.value)}
                                                        disabled={!isEditing}
                                                        className="form-input text-sm w-full mt-1 bg-white"
                                                    >
                                                        <option value="sem_limite">Sem Limite</option>
                                                        <option value="valor_maximo">Máx ({currencySymbol})</option>
                                                    </select>
                                                </div>
                                                <div className="col-span-12 md:col-span-8">
                                                    {(faixa.limite?.tipo === 'valor_maximo' || faixa.limite?.type === 'valor_maximo') && (
                                                        <>
                                                            <label className="text-[11px] text-gray-500 uppercase tracking-wider">Valor Máximo ({currencySymbol})</label>
                                                            <input
                                                                type="number"
                                                                value={faixa.limite?.valor || 0}
                                                                onChange={(e) => handleFaixaChange(prof._id, idx, 'limite.valor', parseFloat(e.target.value) || 0)}
                                                                disabled={!isEditing}
                                                                className="form-input text-sm w-full mt-1"
                                                                min="0"
                                                                step="0.01"
                                                            />
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            {prof.comissao.faixas.length > 1 && isEditing && (
                                                <div className="mt-3 flex justify-end">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveFaixa(prof._id, idx)}
                                                        className="text-red-500 hover:text-red-700 text-xs font-medium px-2 py-1 rounded bg-red-50"
                                                    >
                                                        Remover faixa
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    
                                    {isEditing && (
                                        <button
                                            type="button"
                                            onClick={() => handleAddFaixa(prof._id)}
                                            className="text-xs text-primary hover:text-primary-dark font-medium flex items-center mt-2 px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                                        >
                                            <span className="text-xl leading-none mr-1 pb-0.5">+</span> Adicionar Faixa Extra
                                        </button>
                                    )}
                                </div>

                                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 mt-5 pt-4 border-t border-gray-100">
                                    <label className="checkbox-label text-sm">
                                        <input
                                            type="checkbox"
                                            checked={prof.comissao.considerarTaxas || false}
                                            onChange={(e) => handleComissaoChange(prof._id, 'considerarTaxas', e.target.checked)}
                                            disabled={!isEditing}
                                            className="form-checkbox"
                                        />
                                        <span className="checkbox-custom"></span>
                                        <span className="checkbox-label-text text-gray-700">Descontar Taxas do Cartão</span>
                                    </label>
                                    <label className="checkbox-label text-sm">
                                        <input
                                            type="checkbox"
                                            checked={prof.comissao.considerarDescontos !== false}
                                            onChange={(e) => handleComissaoChange(prof._id, 'considerarDescontos', e.target.checked)}
                                            disabled={!isEditing}
                                            className="form-checkbox"
                                        />
                                        <span className="checkbox-custom"></span>
                                        <span className="checkbox-label-text text-gray-700">Descontar Promoções</span>
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="form-content">
            <div className="form-header flex items-start justify-between mb-2">
                <div className="flex-1">
                    <h3 className="form-title flex items-center mb-1 text-primary text-lg">
                        <Users size={22} className="mr-2"/>
                        Profissionais Vinculados
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">Gerencie quem atende nesta unidade</p>
                    
                    {!isEditing && !isLoadingUsers && (
                        <div className="text-xs text-green-600 mt-2 flex items-center bg-green-50 px-2 py-1 rounded-md font-medium w-fit">
                            <CheckCircle size={14} className="mr-1" />
                            {profissionaisVinculados.length} profissional(is) vinculado(s)
                        </div>
                    )}
                    
                    {!roleProfissionalId && !loadingRoles && (
                        <div className="text-xs text-orange-600 mt-2 flex items-center bg-orange-50 p-2 rounded w-fit">
                            <AlertCircle size={12} className="mr-1" />
                            <span>Role profissional não encontrada. Contate o suporte.</span>
                        </div>
                    )}
                </div>

                {!isEditing ? (
                    <button onClick={() => setIsEditing(true)} className="action-button action-button-secondary bg-white border border-gray-300 shadow-sm text-gray-700" disabled={!roleProfissionalId}>
                        <Settings size={16} className="icon text-gray-500" /> Editar Equipe
                    </button>
                ) : (
                    <div className="flex gap-2">
                        <button onClick={() => { setIsEditing(false); handleReload(); }} className="action-button action-button-secondary bg-white border border-gray-300 shadow-sm text-gray-700">
                            <XCircle size={16} className="icon text-gray-500" /> Cancelar
                        </button>
                        <button onClick={handleSave} disabled={isSaving} className="action-button action-button-primary bg-primary text-white shadow-sm">
                            {isSaving ? <Loader2 size={16} className="icon animate-spin" /> : <Save size={16} className="icon" />}
                            Salvar Vínculos
                        </button>
                    </div>
                )}
            </div>

            <div className="form-tabs-content p-0 mt-4 border-t border-gray-100 pt-6">
                {isEditing && (
                    <div className="mb-6 relative px-2 flex gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Buscar profissionais por nome ou email..."
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm shadow-sm"
                            />
                        </div>
                        {/* Antes era um botao Tailwind avulso (largura fixa 130px,
                            cor propria) em vez de action-button-secondary */}
                        <button
                            type="button"
                            onClick={handleReload}
                            className="action-button action-button-secondary"
                        >
                            <RefreshCw size={16} className="icon" /> Atualizar
                        </button>
                    </div>
                )}

                {profissionaisRenderizaveis.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200 mt-4 mx-2">
                        {searchTerm ? 'Nenhum profissional encontrado na busca.' : 'Nenhum profissional disponível na conta.'}
                    </div>
                ) : (
                    <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                        
                        {isEditing && (
                            <div className="bg-gray-50 p-3 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase flex justify-between">
                                <span className="ml-16">Profissional</span>
                                <span className="mr-14">Comissão</span>
                            </div>
                        )}
                        
                        <div className="divide-y divide-gray-100">
                            {/* Mostra vinculados no topo */}
                            {profissionaisRenderizaveis.filter(p => p.isSelected).map(renderProfissionalRow)}
                            
                            {/* Mostra não vinculados em baixo se estiver editando */}
                            {isEditing && profissionaisRenderizaveis.filter(p => !p.isSelected).map(renderProfissionalRow)}
                        </div>
                    </div>
                )}
            </div>

            {showDesvincularModal && vinculoParaDesvincular && (
                <DesvincularUnidadeModal
                    show={showDesvincularModal}
                    onClose={() => {
                        setShowDesvincularModal(false);
                        setVinculoParaDesvincular(null);
                        if (vinculoParaDesvincular?.user) {
                            const idStr = getSafeId(vinculoParaDesvincular.user);
                            if (!selectedProfissionais.includes(idStr)) setSelectedProfissionais([...selectedProfissionais, idStr]);
                        }
                    }}
                    vinculo={vinculoParaDesvincular}
                    userNome={vinculoParaDesvincular.user?.name || ''}
                    unidadeNome={unidade?.nome}
                    onSuccess={handleDesvincularSuccess}
                />
            )}
        </div>
    );
};

export default UnidadeProfissionaisSection;